import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Admin } from '@/types/api'
import { authService } from '@/services/authService'
import { toast } from 'react-hot-toast'
import i18n from '@/i18n'

interface AuthState {
  admin: Admin | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  getCurrentAdmin: () => Promise<void>
  initializeAuth: () => Promise<void>
  clearError: () => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      admin: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await authService.login({ username, password })
          
          console.log('Login successful, setting auth state:', response.admin)
          
          set({
            admin: response.admin,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })

          toast.success(i18n.t('auth:welcomeMessage', { name: response.admin.name }))
          
          // 디버깅: 상태 업데이트 후 확인
          setTimeout(() => {
            const currentState = get()
            console.log('Auth state after login:', {
              isAuthenticated: currentState.isAuthenticated,
              admin: currentState.admin
            })
          }, 100)
        } catch (error: any) {
          set({
            admin: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || i18n.t('auth:loginFailed'),
          })
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })

        try {
          await authService.logout()
        } catch (error) {
          console.error('Logout error:', error)
        } finally {
          set({
            admin: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
          
          // Redirect to login page
          window.location.href = '/login'
        }
      },

      getCurrentAdmin: async () => {
        if (!authService.isAuthenticated()) {
          return
        }

        set({ isLoading: true, error: null })

        try {
          const admin = await authService.getCurrentAdmin()
          set({
            admin,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error: any) {
          set({
            admin: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message,
          })
          throw error
        }
      },

      initializeAuth: async () => {
        const currentState = get()
        
        // 이미 초기화 중이면 중복 실행 방지
        if (currentState.isLoading) {
          return
        }

        // isAuthenticated가 true인데 admin이 null인 경우 (localStorage 불일치) 재로드 필요
        if (currentState.isAuthenticated && currentState.admin) {
          console.log('🔔 AuthGuard: 이미 인증된 상태입니다', { admin: currentState.admin })
          return
        }

        if (!authService.isAuthenticated()) {
          set({ isLoading: false, isAuthenticated: false, admin: null })
          return
        }

        set({ isLoading: true })

        try {
          const admin = await authService.getCurrentAdmin()
          console.log('🔔 AuthGuard: admin 정보 로드 완료', { admin })
          set({
            admin,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error: any) {
          console.error('Auth initialization error:', error)

          // 네트워크/CORS/503 등 일시적인 오류일 때는 세션을 유지하고,
          // 진짜 인증 오류(401 계열)는 인터셉터의 handleUnauthorized에 맡긴다.
          const message = error?.message || i18n.t('common:errors.serverUnreachable')

          set((state) => ({
            // 기존 admin / isAuthenticated 값은 그대로 두고 로딩만 해제
            admin: state.admin,
            isAuthenticated: state.isAuthenticated,
            isLoading: false,
            error: message,
          }))
        }
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'admin-auth',
      partialize: (state) => ({
        admin: state.admin,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)